import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Subject } from 'rxjs';
import { Color } from '../class/Color';
import { Device } from '../class/Device';
import { HarshEvent } from '../class/HarshEvent';
import { TripCompare } from '../class/TripCompare';
import { Trip, TripsHistory } from '../class/TripsHistory';
import { TripBack } from '../class/TripsHistory/types';
import { DEVICE_HISTORY } from '../component/devices/devices-routing.constants';
import { VerticalTimelineItemComponent } from '../component/history/vertical-timeline/vertical-timeline-item/vertical-timeline-item.component';
import { DRIVER_HISTORY } from '../component/mobility-tab/drivers-sub-tab/drivers-routing.constants';
import { Driver } from '../database/models';
import { DateHelper } from '../utils/DateHelper';
import { ApiService } from './api.service';
import { BaseService } from './base.service';
import DevicesStatsService from './devices-stats.service';
import DriversService from './drivers.service';
import { FuelTypeService } from './fueltype.service';
import { GasStationEventsService } from './gas-station-events.service';
import { HarshEventService } from './harshevent.service';
import { MapService } from './map.service';
import { SensorsListService } from './sensorslist.service';
import { AccountStateService } from './account-state.service';
import { AuthService } from './auth.service';
import { DevicesManagerService } from './devices-manager.service';
import { SnackBarService } from './snack-bar.service';
import { TripCompareStateService } from './trip-compare-state.service';
import { TripStateService } from './trip-state.service';
import { ViewService } from './view.service';

type Range = {
  from: number;
  to: number;
};

type LoadArgs = {
  type: TripsHistory['type'];
  item: Device | Driver; // device or driver uid
  range: Range;
  isCompare?: boolean;
  tripCompareId?: number;
  color?: string;
};

interface TripCompareArgs {
  entityType: string;
  entity: Device | Driver; // driver or device
  range: Range;
  tripCompareId?: number;
  color?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TripService extends BaseService<TripsHistory> {
  public table: boolean;
  public selectedTripsCompare$ = new BehaviorSubject<number>(undefined);
  public selectTemperature = 0;
  public loadData = signal(false);
  public get $trips() {
    return this._tripStateService.$trips;
  }
  public get $selectedTrip() {
    return this._tripStateService.$selectedTrip;
  }

  public updateTrips(updater: (trips: TripsHistory) => TripsHistory): void {
    this._tripStateService.updateTrips(updater);
  }

  public clearSelectedTrip(): void {
    this._tripStateService.setSelectedTrip(null);
  }

  public $selectedTrips = signal<Trip[]>(null);
  public $selectedStopPoint = signal<Trip>(null);
  private _range: { from: number; to: number };
  public unselectTrip$ = new Subject<void>();

  private _devicesManagerService = inject(DevicesManagerService);
  private _authService = inject(AuthService);
  private _accountStateService = inject(AccountStateService);
  private mapService = inject(MapService);
  private _tripCompareStateService = inject(TripCompareStateService);
  private _tripStateService = inject(TripStateService);
  private router = inject(Router);
  private harshEventService = inject(HarshEventService);

  private snackBarService = inject(SnackBarService);
  private _gasStationEventsService = inject(GasStationEventsService);
  private _viewService = inject(ViewService<'map' | 'table'>);
  private _driversService = inject(DriversService);

  private _devicesStatsService = inject(DevicesStatsService);
  private _fuelTypeService = inject(FuelTypeService);
  private _sensorsListService = inject(SensorsListService);

  constructor(apiService: ApiService = inject(ApiService)) {
    super(apiService);
    this.mapService.tripEvent$.subscribe((uid_trip: number) => this.selectTrip(uid_trip));
    this.mapService.tripCompareEvent$.subscribe((uid: number) => this.highlightTripCompare(uid));
  }

  private _onLoadError({ error, tripType, uid }) {
    console.error(error);

    const deviceOfDriver = tripType === 'drivertrip' ? this._driversService.getDevices(uid)?.[0] : null;

    if (this._devicesManagerService.get(uid) && tripType === 'trip') {
      this.mapService.fitOn(
        this._devicesManagerService.get(uid).latitude,
        this._devicesManagerService.get(uid).longitude
      );
    } else if (tripType === 'drivertrip' && deviceOfDriver) {
      this.mapService.fitOn(deviceOfDriver.latitude, deviceOfDriver.longitude);
    }
  }

  private _addHarshEvents(events: Record<number, HarshEvent>) {
    const trips = this.$trips().trips;
    for (const id in events) {
      const tripIndex = trips.findIndex(trip => trip.uid === events[id].uid_trip);

      if (tripIndex >= 0) {
        trips[tripIndex].events.push({ type: 'harsh', values: events[id] });
      }
    }
  }

  private _getParams(args: { type: string; uid: number; range: Range }) {
    if (args.type === 'trip') {
      return {
        token: this._authService.$token(),
        from: args.range.from,
        to: args.range.to,
        sensor: this._haveSensor(args.uid) ? 1 : 0,
        uid: args.uid,
      };
    }
    return {
      token: this._authService.$token(),
      from: args.range.from,
      to: args.range.to,
      uid_driver: args.uid,
    };
  }

  private _haveSensor(uid: number) {
    return this._sensorsListService.$values().some(sensor => Number(sensor.uid_device) === Number(uid));
  }

  public override async HandleResponse(response: JSON): Promise<TripsHistory> {
    if (response?.['temperature']) {
      return response?.['temperature']?.['data'];
    }
    const type = response?.['drivertrip'] ? 'drivertrip' : 'trip';
    const apiData: TripBack[] = response?.[type]?.['data'];

    if (apiData?.length > 0) {
      const device = this._devicesManagerService.get(apiData[0].uid_device);
      const deviceStats = this._devicesStatsService.getByDeviceId(apiData[0].uid_device);
      const fuel_type = deviceStats?.fuel_type;

      return new TripsHistory({
        trips: apiData,
        type,
        device: {
          uid: device.uid,
          label: device.registration,
          type: device.device_type,
          avr_fuel_consumption: deviceStats.avr_fuel_consumption,
          co2_emissions_type: this._fuelTypeService.$values()?.[fuel_type]?.co2_emissions,
          isTrackOrSecureTrack: deviceStats.isInDeviceModes(['SECURE&TRACK', 'TRACK']),
        },
        range: this._range,
        gasStationEvents: this._gasStationEventsService.events(),
      });
    }

    return new TripsHistory({
      trips: apiData,
      type,
      device: null,
      range: this._range,
      gasStationEvents: this._gasStationEventsService.events(),
    });
  }

  /**
   * Calculate the cumulative usage time of nano2 across all trips in milliseconds.
   *
   * Each character in the `nano2_usage_time` string represents a 5-minute interval. The total usage time is calculated by summing the number of '1's in the binary representation of `nano2_usage_time` for all trips and then multiplying by 5 minutes (converted to milliseconds).
   */
  public cumulativeUsageTimeNano2 = computed(() => {
    const MINUTE_BY_SLICE = 5; // 5 minutes
    const trips = this.$trips()?.trips;
    if (!trips) return 0;

    const totalUsageTime = trips.reduce((total, trip) => {
      const binaryArray = [...trip.nano2_usage_time];
      const sumFromBinary = binaryArray.reduce((acc, char) => acc + Number(char), 0);
      return total + sumFromBinary;
    }, 0);

    return totalUsageTime * MINUTE_BY_SLICE * 60 * 1000;
  });

  private _computeActivityTime(nano2_usage_time: string): number {
    return (
      [...nano2_usage_time].reduce((acc, char) => acc + Number(char), 0) *
      this.MINUTE_BY_SLICE *
      this.SECOND_BY_MINUTE *
      1000
    );
  }

  MINUTE_BY_SLICE = 5; // 5 minutes
  SECOND_BY_MINUTE = 60;

  public cumulativeUsageTimeByDayNano2 = computed(() => {
    let currentdailyTrip: number | null = null;
    let currentObject: {
      numberDay: number;
      timestamp: number;
      usage_time: number;
    } | null = null;

    const result: { numberDay: number; timestamp: number; usage_time: number }[] = [];

    this.$trips()?.trips.forEach((trip, index) => {
      if (trip.dailyId && trip.dailyId !== currentdailyTrip) {
        currentdailyTrip = trip.dailyId;
        currentObject = null;

        currentObject = {
          numberDay: new DateHelper(trip.stopPoint.timestamp * 1000).getDayOfWeek(),
          timestamp: new DateHelper(trip.stopPoint.timestamp * 1000).startOfDay().timestamp(),
          usage_time: this._computeActivityTime(trip.nano2_usage_time),
        };

        if (this.$trips().trips[index + 1]?.dailyId || !this.$trips().trips[index + 1])
          result.push(currentObject);
      } else {
        if (currentObject) {
          currentObject.usage_time += this._computeActivityTime(trip.nano2_usage_time);
          if (this.$trips().trips[index + 1]?.dailyId || !this.$trips().trips[index + 1])
            result.push(currentObject);
        }
      }
    });
    return result;
  });

  private _rangeDays = computed(() => {
    const from = this.$trips()?.from || 0;
    const to = this.$trips()?.to || 0;
    return DateHelper.daysBetweenTimestamps(from, to, { inclusive: true });
  });

  /**
   * Returns an entry for every day in the selected range, with `usage_time` set to `null` for days with no recorded trips.
   */
  public usageTimeByDayWithEmptyDays = computed(() => {
    const tripsByTimestamp = new Map(
      this.cumulativeUsageTimeByDayNano2().map(trip => [trip.timestamp, trip])
    );
    const startOfDay = new DateHelper(this.$trips()?.from || 0).startOfDay();

    return Array.from({ length: this._rangeDays() }, (_, index) => {
      const day = startOfDay.addDays(index);
      const trip = tripsByTimestamp.get(day.timestamp());
      return {
        numberDay: day.getDayOfWeek(),
        timestamp: day.timestamp() * 1000,
        usage_time: trip?.usage_time ?? null,
      };
    });
  });

  /**
   * Adds empty slots at the beginning and end to align data to a full week. (only nano2 secure&track and track)
   */
  public usageTimeByDayWithPadding = computed(() => {
    const DAYS_IN_WEEK = 6; // Monday to Sunday (0 to 6)

    const start = new DateHelper(this.$trips()?.from || 0).getDayOfWeek();
    const end = DAYS_IN_WEEK - new DateHelper(this.$trips()?.to || 0).getDayOfWeek();

    const startnNullDays = Array(start).fill(null);
    const endnNullDays = Array(end).fill(null);

    return [...startnNullDays, ...this.usageTimeByDayWithEmptyDays(), ...endnNullDays];
  });

  public async load({ type, item, range, isCompare = false, tripCompareId = null, color = null }: LoadArgs) {
    this._range = range;
    this.loadData.set(true);
    try {
      const tripsHistory = await lastValueFrom(
        this.Get(type, this._getParams({ type, range, uid: item.uid }))
      );

      this._tripStateService.setTrips(tripsHistory);

      if (this.$trips()) {
        if (this._accountStateService.$account().isSubsEco) {
          const harshEvents = await this.harshEventService.loadEvent({ type, uid: item.uid, range });

          if (harshEvents) {
            this._addHarshEvents(harshEvents);

            if (!isCompare) {
              this.mapService.createMarkerEventEco(harshEvents);
            }
          }
        }

        if (isCompare) {
          this.addTripCompare({
            entityType: type,
            entity: item,
            range: { from: range.from, to: this.$trips().daysNumber > 1 ? range.to : range.from },
            tripCompareId,
            color,
          });
        } else if (this.$trips().forcedPrivateTrip) {
          this.mapService.createPrivateCircleLayer(
            this.$trips().trips,
            this._accountStateService.$account().privacy_blur_radius
          );
          // need for display number on map for next trips
          this._tripStateService.updateTrips(current => {
            current.trips = [];
            return current;
          });
          this.mapService.addTripLayer(this.$trips(), true);
        } else {
          const filteredTrips = [];
          // filter trip that is into forced privacy
          for (const trip of this.$trips().trips) {
            if (!trip.hasForcedPrivacy) {
              filteredTrips.push(trip);
            }
          }
          this._tripStateService.updateTrips(current => {
            current.trips = filteredTrips;
            return current;
          });
          this.mapService.addTripLayer(this.$trips(), true);
        }
      }
      this.loadData.set(false);
    } catch (error) {
      this._onLoadError({ error, tripType: type, uid: item.uid });
    }
    this.loadData.set(false);
  }

  public clearServiceData() {
    this._tripStateService.clear();
    this.harshEventService.clear();
  }

  public tryGoTimestamp(item: Device | Driver, firstDate: number, lastDate: number, url: string) {
    const startDate = new DateHelper(firstDate);
    const endDate = new DateHelper(lastDate);
    const tomorrow = new DateHelper().addDays(1);
    const endDateFinish = endDate.endOfDay();

    if (
      startDate.timestamp() > tomorrow.timestamp() ||
      startDate.timestamp() > endDate.timestamp() ||
      endDate.timestamp() > tomorrow.timestamp()
    ) {
      return this.snackBarService.send({
        message: 'SNACK_BAR.INVALID_DATE',
        type: 'error',
      });
    } else {
      // garde fou pour eviter des requetes trop grosse // amplitude de deux mois
      const testPeriod = new DateHelper(new DateHelper(startDate).endOfDay()).addMonths(2);
      if (endDateFinish > testPeriod) {
        this.snackBarService.send({
          message: 'SNACK_BAR.MULTI_TRIP',
          type: 'error',
        });
        return;
      } else {
        const computedUrl =
          url === 'trip'
            ? DEVICE_HISTORY(item.uid_parent, item.uid)
            : DRIVER_HISTORY(item.uid_parent, item.uid);
        this.router.navigate([computedUrl], {
          queryParams: {
            from: firstDate,
            to: lastDate,
            view: this._viewService.currentView(),
          },
        });
      }
    }
  }

  public addTripCompare(args: TripCompareArgs) {
    const tripsCompare = this._tripCompareStateService.$tripsCompare();
    const tripCompare = args.tripCompareId
      ? new TripCompare(tripsCompare?.datas?.[args.tripCompareId])
      : new TripCompare();
    const arrayKeys = tripsCompare?.datas
      ? Object.keys(tripsCompare.datas)
          .map(Number)
          .sort((a, b) => a - b)
      : [];
    let id;
    let colorFinal;

    if (arrayKeys.length > 0) id = arrayKeys[arrayKeys.length - 1] + 1;
    else id = 1;

    if (tripCompare.color === '#000') colorFinal = args.color || Color.randomColor();
    else colorFinal = tripCompare.color;

    tripCompare.uid = args.tripCompareId ?? id;
    tripCompare.driver = this._driversService.$valuesRecord()?.[args.entity.uid];
    tripCompare.device = this._devicesManagerService.get(args.entity.uid);
    tripCompare.from = args.range.from;
    tripCompare.to = args.range.to;
    tripCompare.trips = this.$trips();

    tripCompare.color = colorFinal;
    this._tripCompareStateService.$tripsCompare.update(current => ({
      ...current,
      datas: { ...current?.datas, [tripCompare.uid]: tripCompare },
    }));
    this.mapService.drawtripCompare(this._tripCompareStateService.$tripsCompare());
  }

  public selectTrip(uid: number) {
    if (!this.$trips()?.trips?.length) return;
    if (this.$selectedTrip()?.uid === uid) {
      this._tripStateService.setSelectedTrip(null);
      this.mapService.highlightTrip(null, null);
      this.unselectTrip$.next();
      return;
    }

    if (this.$selectedStopPoint()) {
      // reset selection for selected trip stop point
      this.$selectedStopPoint.set(null);
      this.mapService.hTripStopPointMarker.remove();
    }

    const trips = [...this.$trips().trips];
    if (this.$trips().previousTrip) trips.unshift(this.$trips().previousTrip);

    const newTripIndex = trips.findIndex(trip => trip.uid === uid);
    const newTrip = trips[newTripIndex];
    const nextTrip = trips[newTripIndex + 1];

    this._tripStateService.setSelectedTrip(newTrip);
    this.mapService.highlightTrip(this.$selectedTrip(), nextTrip);

    if (this.mapService.$isDisplayEcoEvent()) {
      const trip = this.$trips().trips.find(trip => trip.uid === uid);
      this.mapService.highlightEco(trip);
    }
  }

  // It's used to keep the stopType of the selected stop point because first item in timeline has two stop points (departure and stop)
  public stopType: VerticalTimelineItemComponent['stopClickType'] | null = null;

  public selectTripPoint(trip: Trip, stopType: VerticalTimelineItemComponent['stopClickType']) {
    // reset selection if stop point is already selected and the same type
    if (this.$selectedStopPoint()?.uid === trip.uid && this.stopType === stopType) {
      this.$selectedStopPoint.set(null);
      this.mapService.hTripStopPointMarker.remove();
    } else {
      // reset selection for selected trip if it is the case
      if (this.$selectedTrip()) {
        // reset selection for selected trip
        this._tripStateService.setSelectedTrip(null);
        this.mapService.highlightTrip(null, null);
      }

      this.mapService.fitOnTripPoint({
        longitude: trip.stopPoint.coords.longitude,
        latitude: trip.stopPoint.coords.latitude,
      });
      this.$selectedStopPoint.set(trip);
      this.stopType = stopType;
    }
  }

  public highlightTripCompare(uid_compare?: number) {
    this.selectedTripsCompare$.next(
      uid_compare === this.selectedTripsCompare$.getValue() ? undefined : uid_compare
    );
    this.mapService.highlightTripCompare(uid_compare);
    if (this._accountStateService.$account().isSubsEco) this.mapService.highlightEcoCompare(uid_compare);
  }

  public async getTemperature(trips: TripsHistory): Promise<any> {
    return new Promise((resolve, reject) => {
      let data: any;
      this.Get('temperature', {
        token: this._authService.$token(),
        uid_device: trips.device.uid,
        from: trips.from,
        to: trips.to,
      }).subscribe(
        value => {
          data = value;
        },
        err => {
          reject(err);
        },
        () => {
          resolve(data);
        }
      );
    });
  }
}
